import {
  WorkerRunTableFilterArea,
  WorkerRunTableFiltersType,
  WorkerRunTableTitleDecorationFilterArea,
} from "#src/batteries-included-components/FilterAreas/WorkerRunsFilterAreas";
import { KeyValueTags } from "#src/batteries-included-components/KeyValueTags/KeyValueTags";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";
import {
  useGetManyWorkers,
  useListWorkerRuns,
} from "#src/components/hooks/adapters/useWorkers";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useStickyState } from "#src/hooks/useStickyState";
import { WorkerDetailRoutePath } from "#src/routes/workers/definitions/[workerId]/detail";
import { WorkerRunDetailRoutePath } from "#src/routes/workers/runs/[runId]";
import {
  WorkerRunStatusToPillVariantMapping,
  formatWorkerRunDuration,
  getWorkerRunIdDisplayLabel,
} from "#src/view-models/workers";
import {
  DataTable,
  DataTablePanel,
  HeaderType,
  Icon,
  Pill,
  StorageKeys,
  Tooltip,
} from "@validereinc/common-components";
import {
  type GetListWorkerRunFiltersType,
  SortDirection,
  type UserReturnType,
  UserTypeEnum,
  WORKER_RUN_STATUS_VALUES,
  WorkerRunStatus,
  type WorkerRunType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useMemo } from "react";

/**
 * Returns true when no status filter is applied (show all statuses).
 */
const shouldShowAllStatuses = (
  status: WorkerRunTableFiltersType["status"]
): boolean => {
  if (status == null) return true;
  const arr = Array.isArray(status) ? status : [status];
  return arr.length === 0 || arr.includes("");
};

/**
 * Maps table filter storage to `/worker_runs/search` filter payload.
 * Status is always sent as a string array when narrowed so `getFilterObject` produces `$in`
 * (a lone string would become `$like` for non-UUID values).
 * All three statuses selected is equivalent to no status filter.
 */
const buildGetListWorkerRunFilters = (
  tableFilters: WorkerRunTableFiltersType
): GetListWorkerRunFiltersType => {
  const filters: GetListWorkerRunFiltersType = {};
  if (tableFilters.workerId != null && tableFilters.workerId !== "") {
    filters.worker_id = tableFilters.workerId;
  }
  if (
    tableFilters.version != null &&
    typeof tableFilters.version === "number" &&
    !Number.isNaN(tableFilters.version)
  ) {
    filters.version = tableFilters.version;
  }
  if (!shouldShowAllStatuses(tableFilters.status)) {
    const status = tableFilters.status;
    const arr: string[] = Array.isArray(status)
      ? status
      : status != null && status !== ""
        ? [status]
        : [];
    const concrete = arr.filter(
      (status): status is WorkerRunType["status"] =>
        status === WorkerRunStatus.STARTED ||
        status === WorkerRunStatus.COMPLETED ||
        status === WorkerRunStatus.FAILED
    );
    if (concrete.length === 0) return filters;
    if (concrete.length === WORKER_RUN_STATUS_VALUES.length) return filters;
    filters.status = concrete;
  }
  return filters;
};

export type WorkerRunsTablePanelProps = Pick<
  StorageKeys,
  "filterConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
> & {
  /** Optional initial filters (e.g. when embedded on worker detail to scope by workerId). */
  defaultFilters?: Partial<WorkerRunTableFiltersType>;
};

const WorkerRunsTablePanel = ({
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  defaultFilters,
}: WorkerRunsTablePanelProps) => {
  const [tableFilters] = useStickyState<WorkerRunTableFiltersType>(
    { ...defaultFilters } as WorkerRunTableFiltersType,
    filterConfigStorageKey
  );

  const listFilters = useMemo(
    () => buildGetListWorkerRunFilters(tableFilters),
    [tableFilters]
  );

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "started_at",
      sortDirection: SortDirection.DESCENDING,
    },
    listFilters,
    tableStateStorageKey
  );

  const { data: workerRunsData, isLoading: isLoadingWorkerRuns } =
    useListWorkerRuns({
      page: tableState.page,
      pageSize: tableState.pageSize,
      sortBy: tableState.sortBy,
      sortDirection: tableState.sortDirection,
      filters: listFilters,
    });

  const runs = workerRunsData?.data ?? [];

  const uniqueWorkerIds = useMemo(() => {
    const set = new Set<string>();
    runs.forEach((run) => set.add(run.worker_id));
    return Array.from(set);
  }, [runs]);

  const uniqueCreatedByIds = useMemo(() => {
    const set = new Set<string>();
    runs.forEach((run) => set.add(run.created_by));
    return Array.from(set);
  }, [runs]);

  const workerQueries = useGetManyWorkers(uniqueWorkerIds);
  const { data: usersData, isLoading: isLoadingUsers } = useListUsers(
    {
      filters: {
        id: uniqueCreatedByIds,
        type: [UserTypeEnum.human, UserTypeEnum.machine],
      },
    },
    { enabled: uniqueCreatedByIds.length > 0 }
  );

  const isLoadingWorkers = workerQueries.some((q) => q.isLoading);
  const isLoading =
    isLoadingWorkerRuns ||
    isLoadingWorkers ||
    (uniqueCreatedByIds.length > 0 && isLoadingUsers);

  const workersMap = useMemo(() => {
    const map: Record<string, { name: string }> = {};
    workerQueries.forEach((query, i) => {
      const worker = query.data;
      const id = uniqueWorkerIds[i];
      if (id && worker) map[id] = { name: worker.name };
    });
    return map;
  }, [workerQueries, uniqueWorkerIds]);

  const usersMap = useMemo(() => {
    const list = usersData?.data ?? [];
    return Object.fromEntries(list.map((u) => [u.id, u])) as Record<
      string,
      UserReturnType
    >;
  }, [usersData]);

  const hasWorkerIdInFilters = !!defaultFilters?.workerId;
  const hasVersionInFilters = !!defaultFilters?.version;

  const headers: Array<HeaderType<WorkerRunType>> = [
    {
      key: "id",
      label: "Run",
      isSortable: false,
      renderComponent: ({ item }) => (
        <RoutingLink
          to={WorkerRunDetailRoutePath.toLinkParts({
            pathParams: { runId: item.id },
          })}
        >
          {getWorkerRunIdDisplayLabel(item.id)}
        </RoutingLink>
      ),
    },
    ...(hasWorkerIdInFilters || hasVersionInFilters
      ? []
      : [
          {
            key: "name",
            label: "Name",
            isSortable: false,
            renderComponent: ({ item }) => {
              const worker = workersMap[item.worker_id];
              const name = worker?.name ?? item.worker_id;
              return (
                <RoutingLink
                  to={WorkerDetailRoutePath.toLinkParts({
                    pathParams: { workerId: item.worker_id },
                  })}
                >
                  {name}
                </RoutingLink>
              );
            },
          },
        ]),
    ...(hasVersionInFilters
      ? []
      : [
          {
            key: "version",
            label: "Version",
            isSortable: true,
            renderComponent: ({ item }) => (
              <RoutingLink
                to={WorkerDetailRoutePath.toLinkParts({
                  pathParams: {
                    workerId: item.worker_id,
                  },
                  queryParams: { version: `${item.version}` },
                })}
              >
                v{item.version}
              </RoutingLink>
            ),
          },
        ]),
    {
      key: "status",
      label: "Status",
      isSortable: true,
      renderComponent: ({ item }) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Pill variant={WorkerRunStatusToPillVariantMapping[item.status]}>
            {toTitleCaseString(item.status, { intent: "ui" })}
          </Pill>
          {item.status === WorkerRunStatus.FAILED && item.error_message && (
            <Tooltip
              content={item.error_message}
              trigger="hover"
            >
              <span style={{ cursor: "help", display: "inline-flex" }}>
                <Icon
                  variant="info"
                  size={16}
                />
              </span>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      key: "inputs",
      label: "Input",
      isSortable: false,
      renderComponent: ({ item }) => (
        <KeyValueTags
          record={item.inputs}
          shouldHideFileIcon
        />
      ),
    },
    {
      key: "outputs",
      label: "Output",
      isSortable: false,
      renderComponent: ({ item }) => (
        <KeyValueTags
          record={item.outputs}
          shouldHideFileIcon
        />
      ),
    },
    {
      key: "started_at",
      label: "Started at",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.started_at}
          withTime
          isLoading={isLoading}
        />
      ),
    },
    {
      key: "duration",
      label: "Duration",
      isSortable: false,
      renderComponent: ({ item }) => formatWorkerRunDuration(item),
    },
    {
      key: "created_at",
      label: "Created at",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          withTime
          isLoading={isLoading}
        />
      ),
    },
    {
      key: "created_by",
      label: "Created by",
      isSortable: false,
      renderComponent: ({ item }) => {
        const user = usersMap[item.created_by];
        return (
          <UserDataDisplay
            id={item.created_by}
            name={user?.name ?? "—"}
            isLoading={isLoading}
          />
        );
      },
    },
  ];

  return (
    <DataTablePanel
      panelProps={{
        title: "Runs",
        titleDecorator: (
          <WorkerRunTableTitleDecorationFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
          />
        ),
      }}
      filterComponent={
        hasWorkerIdInFilters ? null : (
          <WorkerRunTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
          />
        )
      }
      storageKey={tableConfigStorageKey}
      dataTableProps={{
        intent: "simplicity-first",
        isLoading,
        headers,
        items: runs,
        getItemId: (item) => item.id,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: workerRunsData?.total_entries ?? 0,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
      }}
    />
  );
};

export default WorkerRunsTablePanel;
